import React, { useCallback, useEffect, useRef, useState } from 'react';
import Chevron from './Icons/Chevron';
import GradientIcon from './Icons/GradientIcon';
import { ConfiguableSvgProps } from './Icons/types';

export type StyledSelectProps = {
	id: string;
	className?: string;
	selection: string | number;
	options: {
		value: any;
		label: string;
		icon?: (props: ConfiguableSvgProps) => JSX.Element;
	}[];
	onChange: (value: string | number) => void;
	style?: React.CSSProperties;
	defaultLabel?: string; // Displays this string if value '' is selected
	noStyle?: boolean;
	small?: boolean;
	maxHeight?: number;
};

const StyledSelect = (props: StyledSelectProps) => {
	const [selectedOption, setSelectedOption] = useState(
		props.options.find((option) => option.value === props.selection) ??
			props.options[0]
	);

	useEffect(() => {
		setSelectedOption(
			props.options.find((option) => option.value === props.selection) ??
				props.options[0]
		);
	}, [props.selection, props.options]);

	const [showPopup, setShowPopup] = useState(false);

	const toggleShowPopup = () => {
		setShowPopup(!showPopup);
	};

	const popupRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);

	const setPopupRef = useCallback((node) => {
		popupRef.current = node;
	}, []);
	const setWrapperRef = useCallback((node) => {
		wrapperRef.current = node;
	}, []);

	useEffect(() => {
		window.addEventListener('mousedown', handleClick);

		return () => window.removeEventListener('mousedown', handleClick);
	}, [showPopup]);

	const handleClick = (clickEvent: PointerEvent) => {
		const newFocus = clickEvent.target as Element;

		if (!showPopup) return;

		// if (!popupRef.current) return;

		// check if the blur occured from a click inside of wrapper
		if (
			wrapperRef.current.contains(newFocus) ||
			wrapperRef.current === newFocus
		) {
			return;
		}

		setShowPopup(false);
	};

	return (
		<div className="relative" ref={setWrapperRef}>
			{props.noStyle ? (
				<button
					id={props.id ?? ''}
					className={`${props.className}`}
					onClick={toggleShowPopup}
					style={props.style}
				>
					<div className="flex items-center justify-between w-full px-2">
						<div className="flex items-center">
							{selectedOption.icon && (
								<selectedOption.icon width="16" height="16" color={'#FFFFFF'} />
							)}
							<span>{selectedOption.label}</span>
						</div>

						<Chevron
							direction={showPopup ? 'up' : 'down'}
							className="w-3 h-3 ml-1 -mr-1"
						/>
					</div>
				</button>
			) : props.small ? (
				<div
					id={props.id ?? ''}
					className="flex items-center text-xs hover:cursor-pointer text-text-interactive-selected"
					onClick={toggleShowPopup}
				>
					<div>{selectedOption.label}</div>
					<div>
						<GradientIcon
							Icon={Chevron}
							direction={showPopup ? 'up' : 'down'}
						/>
					</div>
				</div>
			) : (
				<div
					id={props.id ?? ''}
					onClick={toggleShowPopup}
					style={props.style}
					className={`flex items-center justify-between px-2 py-0.5 rounded-sm border border-input-border bg-button-secondary-bg hover:bg-button-secondary-bg-hover hover:cursor-pointer ${props.className}`}
				>
					<div className="flex items-center space-x-1 text-text-interactive-selected">
						{selectedOption.icon && (
							<selectedOption.icon
								width="16"
								height="16"
								color={'var(--text-interactive-selected)'}
							/>
						)}
						<span>{selectedOption.label}</span>
					</div>

					<Chevron
						direction={showPopup ? 'up' : 'down'}
						className="w-3 ml-2"
						colour="var(--text-interactive-selected)"
					/>
				</div>
			)}

			{showPopup && (
				<div
					className={`absolute max-h-[15rem] w-[150px] overflow-auto translate-y-full -bottom-2 z-100 thin-scroll bg-tabs-default-fill rounded-sm flex flex-col border border-container-border shadow-md`}
					style={
						props.maxHeight
							? { maxHeight: `${props.maxHeight}px`, left: '-4rem' }
							: {}
					}
					ref={setPopupRef}
				>
					{props.options.map((option) => {
						return (
							<div
								key={option.label}
								onClick={() => {
									props.onChange(option.value);
									setShowPopup(false);
								}}
								className={`relative hover:cursor-pointer w-full hover:bg-tabs-hover ${
									option.value === selectedOption.value
										? 'text-text-emphasis bg-tabs-selected'
										: 'text-text-label bg-tabs-default-fill'
								} text-xs pl-1.5 pr-2.5 py-1 flex items-center`}
							>
								{option.value === selectedOption.value && (
									<span className="absolute left-0 h-full w-[2px] bg-purple-50" />
								)}
								{option.icon && (
									<option.icon
										width="16"
										height="16"
										color={
											option.value === selectedOption.value
												? 'var(--text-emphasis)'
												: 'var(--text-label)'
										}
									/>
								)}
								<span className="ml-1">{option.label}</span>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default StyledSelect;
